import React, { useEffect, useState, useCallback, memo } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { StatusTag, Modal } from 'components';
import { Toolbar, Popover as PopoverTwo, Table } from './components';
import { changeStatusDemand } from 'services/changeStatusDemand';
import { IDemandsTable } from 'types/demandsTable';
import { getDemandsData } from 'services/demandsTable';
import { RouteComponentProps } from 'react-router';
import { objToQuery, queryToObj } from 'utils/query';
import { routePaths } from 'constants/routes';
import { formatCurrency } from 'utils/number';
import { demandStatusLabels, demandStatusValues, demandStatusVariant } from 'constants/demands';
import { openNotification } from 'utils/notification';
import { format, parse } from 'date-fns';
import { debounce } from 'utils/debounce';
import useEventBus from 'hooks/useEventBus';
import { getUserSolicitation } from 'services/userSolicitation';
import { getLoggedUser } from 'utils/storage';
import { archidevDemand } from 'services/archidevDemand';

type StatusValueKey = keyof typeof demandStatusValues;

const toDate = (dateString?: string) => {
  if (!dateString) return '-';
  return format(parse(dateString.replace(/T\S+/, ''), 'yyyy-MM-dd', Date.now()), 'dd/MM/yyyy');
};

const switchChangeStatusLabel = (status: string) => {
  if (status === demandStatusValues.pending) {
    return 'Aceitar';
  }

  if (status === demandStatusValues.notStarted) {
    return 'Iniciar';
  }

  if (status === demandStatusValues.ongoing) {
    return 'Finalizar';
  }

  return '';
};

const switchChangeStatusNewValue = (status: string): StatusValueKey => {
  if (status === demandStatusValues.pending) {
    return 'notStarted';
  }

  if (status === demandStatusValues.notStarted) {
    return 'ongoing';
  }

  return 'finished';
};

interface IProps extends RouteComponentProps {}

interface IUserType {
  admin: boolean;
  id: number;
  name: string;
}

const DemandsComp: React.FC<IProps> = ({ history }) => {
  const [data, setData] = useState<IDemandsTable>({ content: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [searchData, setSearchData] = useState(false);

  const eventBus = useEventBus();
  useEffect(() => {
    eventBus.dispatchEvent('onChangeTitle', 'Solicitações');
  }, []);

  const handleGetData = useCallback(async (query?: string) => {
    setIsLoading(true);
    try {
      const clientId = getLoggedUser().client;
      const defaultQuery = `size=10${clientId ? `&clientId=${clientId}` : ''}`;
      let newQuery = `?${defaultQuery}`;

      if (query) {
        if (query[0] === '?') {
          newQuery = `${query}&${defaultQuery}`;
        } else {
          newQuery = `${query}&${defaultQuery}`;
        }
      }

      if (newQuery.includes('?')) {
        newQuery = newQuery.slice(1);
      }

      const response = await getDemandsData(newQuery);
      setData(response.data);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }, []);
  const handlePaginate = (newPage: number) => {
    const newFilterData = queryToObj(history.location.search.slice(1));

    function valuePositive(value: number) {
      const query = localStorage.getItem('queryPageDemands') || '';
      if (value === -1) {
        return 0;
      } else if (value === Number(query)) {
        return Math.abs(Number(query));
      } else {
        return Math.abs(value - 1);
      }
    }

    setTimeout(() => {
      localStorage.removeItem('queryPageDemands');
    }, 1000);

    const query = objToQuery({ ...newFilterData, page: valuePositive(newPage) });
    if (
      newFilterData.page &&
      typeof newFilterData.page === 'string' &&
      newPage - 1 === parseInt(newFilterData.page)
    )
      return;

    history.replace({
      pathname: routePaths.demands,
      search: `${query}`,
    });
  };

  const handleChangeStatus = useCallback(
    debounce(async (demandId: number, newStatus: StatusValueKey) => {
      try {
        await changeStatusDemand(demandId, demandStatusValues[newStatus]);
        handleGetData(history.location.search);

        openNotification('Status alterado com sucesso');
      } catch (error) {}
    }, 300),
    [],
  );

  const getUserType = () => {
    const userType = localStorage.getItem('@dpohub/user');

    if (typeof userType === 'string') {
      const userTp: IUserType = JSON.parse(userType);

      return {
        admin: userTp.admin,
        id: userTp.id,
        name: userTp.name,
      };
    }
  };

  const archivedOldDemand = async (idDemand: string, archidev: boolean) => {
    try {
      await archidevDemand(idDemand, archidev);
      openNotification('Solicitação arquivada!', 'success');
      setTimeout(() => {
        setSearchData(!searchData);
      }, 2000);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getUserType();
    handleGetData(history.location.search.slice(1));
    const removeListen = history.listen(listener => {
      handleGetData(listener.search);
    });
    return () => {
      removeListen();
    };
  }, [searchData]);

  interface Idata {
    data: {
      id: number;
      solicitacaoId: number;
      userId: number;
      name: string;
      admin: true;
    };
  }

  return (
    <Box w="100%">
      <Toolbar history={history} />
      <Box padding="0.1rem 0rem 1rem" background="#fff" borderRadius="10px">
        <Table
          loading={isLoading}
          row={[0]}
          headers={[
            'Pasta',
            'Cliente',
            'Demanda',
            'Serviço',
            'Data',
            'Prazo',
            'Advogado',
            'Horas',
            'Valor',
            'Status',
          ]}
          sizes={[0.3, 1, 1, 1.5, 1, 1, 1, 0.5, 0.7, 0.8]}
          fullLoading={false}
          handles={data?.content.map(demand => async () => {
            const query = '?page=0';
            const defaultQuery = 'size=10&sort=prazo,asc';
            let newQuery = `?${defaultQuery}`;

            if (query) {
              if (query[0] === '?') {
                newQuery = `${query}&${defaultQuery}`;
              } else {
                newQuery = `?${query}&${defaultQuery}`;
              }
            }

            const userType: Idata = await getUserSolicitation(demand.id);

            const user = userType.data;
            if (user.admin) {
              location.href = `/demands/edit/${demand.id}`;
              setIsLoading(true);
            } else {
              location.href = `clientDemands/details/${demand.id}`;
              setIsLoading(true);
            }
          })}
          buttons={data?.content.map(demand => (
            <PopoverTwo
              title={
                <StatusTag
                  key={demand.status}
                  variant={demand.status ? demandStatusVariant[demand.status] : 'accept'}
                >
                  {demand.status ? demandStatusLabels[demand.status] : '-'}
                </StatusTag>
              }
              key={demand.demanda}
              // disabled={!demand.lawyer || !demand.horas || !demand.valor}
              rows={
                demand.status === 'CANCELADO'
                  ? []
                  : demand.status === 'FINALIZADO'
                  ? [
                      {
                        label: (
                          <Flex w="7rem" flex={1} zIndex="000">
                            <StatusTag variant="accept" demandArchived>
                              Arquivar
                            </StatusTag>
                          </Flex>
                        ),
                        onClick: () => archivedOldDemand(String(demand.id), true),
                      },
                      {
                        label: (
                          <Flex w="7rem" flex={1} zIndex="000">
                            <StatusTag variant="danger">Cancelar</StatusTag>
                          </Flex>
                        ),
                        onClick: () => handleChangeStatus(demand.id, 'canceled'),
                      },
                    ]
                  : [
                      {
                        label: (
                          <Flex w="7rem" flex={1}>
                            <StatusTag
                              variant={
                                demandStatusVariant[
                                  demandStatusValues[
                                    switchChangeStatusNewValue(demand.status || '')
                                  ]
                                ]
                              }
                            >
                              {demand.status && switchChangeStatusLabel(demand.status)}
                            </StatusTag>
                          </Flex>
                        ),
                        onClick: () =>
                          demand.status &&
                          handleChangeStatus(demand.id, switchChangeStatusNewValue(demand.status)),
                        disabled:
                          demand.status === demandStatusValues.canceled ||
                          demand.status === demandStatusValues.toCancel ||
                          demand.status === demandStatusValues.finished ||
                          (demand.status === demandStatusValues.pending &&
                            (!demand.lawyer || !demand.horas || !demand.valor)),
                      },
                      {
                        label: (
                          <Modal
                            typeStatus
                            title="Cancelar"
                            header="Cancelar Solicitação"
                            body="Tem certeza que deseja cancelar esta solicitação?"
                            onClick={() => handleChangeStatus(demand.id, 'canceled')}
                            yesText="Sim"
                            noText="Não"
                          />
                        ),
                        onClick: () => {},
                      },
                    ]
              }
            />
          ))}
          rows={data?.content.map(demand => [
            demand.client.id,
            demand.client.name,

            demand.demanda,
            demand?.serviceType?.name,
            toDate(demand.createdAt),
            toDate(demand.prazo),
            demand.lawyer?.name || '-',
            String(demand?.horas).slice(0, 5) === 'null' ? '-' : String(demand?.horas),
            demand?.valor ? formatCurrency(demand.valor) : '-',
          ])}
          pagination={{
            initialPage: Number(localStorage.getItem('queryPageDemands')) + 1 ?? 1,
            onChange: handlePaginate,
            pagesCount: data?.totalPages || 1,
          }}
        />
      </Box>
    </Box>
  );
};

export const Demands = memo(DemandsComp);
